import { useEffect, useRef, useState, type ReactNode } from "react";
import QRCode from "qrcode";
import { Drawer } from "vaul";
import { TgScreen } from "../components/TgScreen";
import { Card, Skeleton, Spinner } from "../components/atoms";
import { useShieldedKeys } from "../shielded/keys-context";
import { useShieldedBalance } from "../shielded/balance-context";
import { useDevice } from "../lib/device-context";
import {
  pendingTransactions,
  recentTransactions,
  updateTransactionStatus,
  type TxRecord,
  type TxStatus,
} from "../lib/tx-history";
import {
  recentIncomingNotes,
  type IncomingNote,
} from "../lib/incoming-notes";
import { fetchSwapStatus } from "../lib/teleswap-client";
import { getZecUsdPrice, formatUsd, zatsToUsd } from "../lib/price";
import { isBackedUp } from "../lib/backup-state";
import { truncateAddr, formatBalanceZec } from "../lib/format";
import { haptic } from "../lib/haptics";
import { checkZcashTxStatusBatch } from "../lib/zec-chain-status";
import { useEvmHoldings } from "../evm/use-evm-holdings";
import { EVM_CHAINS, type EvmChainId } from "../evm/chains";
import type { ChainBalances, TokenBalance } from "../evm/balances";

/**
 * Unified activity entry — sends, swaps, and incoming notes rendered
 * in one stream. Discriminated by `direction` so the row component can
 * pick the right label and sign.
 */
type ActivityEntry =
  | { direction: "out"; ts: number; tx: TxRecord }
  | { direction: "in"; ts: number; note: IncomingNote };

function buildActivity(txs: TxRecord[], incoming: IncomingNote[]): ActivityEntry[] {
  const out: ActivityEntry[] = txs.map((tx) => ({
    direction: "out",
    ts: tx.broadcasted_at,
    tx,
  }));
  const inn: ActivityEntry[] = incoming.map((n) => ({
    direction: "in",
    ts: n.observed_at,
    note: n,
  }));
  return [...out, ...inn].sort((a, b) => b.ts - a.ts).slice(0, 12);
}

interface Props {
  onOpenBackup?: () => void;
}

export function WalletScreen({ onOpenBackup }: Props = {}) {
  const { keys, state } = useShieldedKeys();
  const {
    balance,
    loading: polling,
    error,
    refresh,
    recoverPendingSpends,
  } = useShieldedBalance();
  const [recovering, setRecovering] = useState(false);
  const [recoverToast, setRecoverToast] = useState<string | null>(null);
  const { assessment, warningDismissed, dismissWarning } = useDevice();
  // Skeleton gating — we render placeholders for the balance card and
  // address card during initial derivation and the very first sync pass.
  // After the first sync completes (lastSyncedHeight > 0) we trust the
  // real numbers even if polling is still flipping on/off.
  const showBalanceSkeleton =
    state.kind === "deriving" ||
    (balance.lastSyncedHeight === 0 && polling);
  // Count-up animation — when a new incoming note arrives during the
  // session, we interpolate from previous → new over ~600ms and apply a
  // brief green flash. On send (balance decreases) or first mount we
  // skip the animation and just snap to the current value.
  const [displayZats, setDisplayZats] = useState<number>(
    () => balance.netBalanceZats,
  );
  const [flashKey, setFlashKey] = useState<number>(0);
  const prevZatsRef = useRef<number>(balance.netBalanceZats);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevZatsRef.current;
    const next = balance.netBalanceZats;
    if (next === prev) return;
    if (next <= prev) {
      // Send/spend, or initial-load decrease — no animation.
      prevZatsRef.current = next;
      setDisplayZats(next);
      return;
    }
    // Increase: animate over 600ms (ease-out) and flash.
    const start = performance.now();
    const duration = 600;
    const from = prev;
    const delta = next - prev;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayZats(Math.round(from + delta * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    prevZatsRef.current = next;
    setFlashKey((k) => k + 1);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [balance.netBalanceZats]);
  const [txs, setTxs] = useState<TxRecord[]>(() => recentTransactions(10));
  const [incoming, setIncoming] = useState<IncomingNote[]>(() =>
    recentIncomingNotes(20),
  );
  const [backedUp, setBackedUp] = useState<boolean>(() => isBackedUp());
  const [zecUsd, setZecUsd] = useState<number | null>(null);
  const [uaCopied, setUaCopied] = useState(false);
  // Activity detail sheet — opens on row tap with full record + explorer links.
  const [detail, setDetail] = useState<
    | { kind: "out"; tx: TxRecord }
    | { kind: "in"; note: IncomingNote }
    | null
  >(null);

  // Pull ZEC/USD price every minute. CoinGecko throttles aggressively
  // on bots, but a single per-user request per minute is well within
  // the free tier's tolerance. Cached at the module level so other
  // components don't re-fetch.
  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      const p = await getZecUsdPrice();
      if (!cancelled) setZecUsd(p);
    };
    void pull();
    const t = setInterval(pull, 60_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // Re-read tx history whenever the wallet polls — covers the case of
  // a send/swap completing while this screen is mounted (no
  // cross-component event bus; localStorage write happens before
  // we navigate back here).
  useEffect(() => {
    const refresh = () => {
      setTxs(recentTransactions(10));
      setIncoming(recentIncomingNotes(20));
      setBackedUp(isBackedUp());
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    // Also re-pull every poll cycle so newly-scanned incoming notes
    // surface without the user needing to tab-switch.
    const t = setInterval(refresh, 30_000);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      clearInterval(t);
    };
  }, []);

  // Poll pending transactions for status. Sends use a simple time
  // heuristic (Zcash blocks are ~75s; two confirmations ~150s gives
  // a reasonable "confirmed" signal until we wire the scanner's
  // on-chain nullifier check). Swaps go through the bot's /swap-status
  // endpoint which proxies to upstream LeoKit.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const pending = pendingTransactions();
      if (pending.length === 0) return;
      let anyChange = false;
      const SEND_CONFIRM_AGE_MS = 150_000;
      // For ZEC swaps that have lingered past this age, also probe a
      // public ZEC indexer directly. If the chain has never seen the
      // tx (the relay accepted-but-didn't-propagate failure mode we
      // hit in prod) we flip the row to "failed" so the user isn't
      // staring at a forever-spinner. 90 s = roughly one ZEC block,
      // long enough to avoid racing a fresh broadcast.
      const ZEC_FAIL_PROBE_AGE_MS = 90_000;
      const zecProbeCandidates: string[] = [];
      for (const tx of pending) {
        if (tx.kind === "send") {
          if (Date.now() - tx.broadcasted_at >= SEND_CONFIRM_AGE_MS) {
            updateTransactionStatus(tx.txid, { status: "confirmed" });
            anyChange = true;
          }
          continue;
        }
        // swap — ask the bot
        const res = await fetchSwapStatus(tx.txid);
        if (!res) continue;
        const newStatus: TxStatus = res.status;
        if (newStatus !== "pending" || res.stage) {
          updateTransactionStatus(tx.txid, {
            status: newStatus,
            swap_stage: res.stage,
          });
          anyChange = true;
          continue;
        }
        // Still pending per the bot — if it's a ZEC-origin swap older
        // than the threshold, queue it for a chain probe below.
        const isZecOrigin = !tx.origin_chain || tx.origin_chain === "zec";
        if (
          isZecOrigin &&
          Date.now() - tx.broadcasted_at >= ZEC_FAIL_PROBE_AGE_MS
        ) {
          zecProbeCandidates.push(tx.txid);
        }
      }
      if (zecProbeCandidates.length > 0) {
        const statuses = await checkZcashTxStatusBatch(zecProbeCandidates);
        for (const txid of zecProbeCandidates) {
          if (statuses.get(txid) === "missing") {
            updateTransactionStatus(txid, {
              status: "failed",
              swap_stage: "broadcast not propagated",
            });
            anyChange = true;
          }
        }
      }
      if (!cancelled && anyChange) setTxs(recentTransactions(10));
    };
    void poll();
    const t = setInterval(() => void poll(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const showDeviceWarning =
    assessment?.class === "weak" && !warningDismissed;

  const hasBalance = balance.netBalanceZats > 0;
  // Three-tier backup urgency, gated on USD value:
  //   - < $1   → no banner (a dust note isn't worth nagging about)
  //   - $1..20 → gentle blue nudge
  //   - ≥ $20  → red urgent banner
  // Fallback: if the price feed failed (zecUsd === null) we revert to
  // the original binary behaviour (any balance = urgent).
  let showBackupBanner = !backedUp;
  let backupBannerUrgent = false;
  if (!backedUp) {
    if (zecUsd != null) {
      const usdValue = (balance.netBalanceZats / 1e8) * zecUsd;
      if (usdValue < 1) {
        showBackupBanner = false;
      } else if (usdValue < 20) {
        showBackupBanner = true;
        backupBannerUrgent = false;
      } else {
        showBackupBanner = true;
        backupBannerUrgent = true;
      }
    } else {
      // Price feed offline — fall back to the simple binary behaviour.
      backupBannerUrgent = hasBalance;
    }
  } else {
    showBackupBanner = false;
  }

  const copyUnifiedAddress = async () => {
    if (!keys) return;
    try {
      await navigator.clipboard.writeText(keys.unifiedAddress);
      setUaCopied(true);
      haptic("success");
      window.setTimeout(() => setUaCopied(false), 2000);
    } catch {
      // best-effort; clipboard might be unavailable in some contexts
    }
  };

  const isEmpty =
    balance.netBalanceZats === 0 &&
    incoming.length === 0 &&
    txs.length === 0;

  return (
    <TgScreen
      title="Shieldz"
      bottomNav="wallet"
      bottomNavHint={isEmpty && keys ? "receive" : undefined}
    >
      <PullToRefresh
        disabled={polling}
        onRefresh={async () => {
          haptic("light");
          try {
            await refresh();
          } catch {
            /* refresh() already routes errors into the balance context */
          }
        }}
      >
      <div style={{ padding: "12px 16px 24px" }}>
        {showBackupBanner && (
          <button
            onClick={() => {
              haptic("light");
              onOpenBackup?.();
            }}
            style={{
              width: "100%",
              padding: 12,
              marginBottom: 12,
              background: backupBannerUrgent
                ? "rgba(239, 68, 68, 0.10)"
                : "rgba(120, 160, 255, 0.07)",
              border: `1px solid ${
                backupBannerUrgent
                  ? "rgba(239, 68, 68, 0.35)"
                  : "rgba(120, 160, 255, 0.20)"
              }`,
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "var(--font-text)",
              color: "var(--text)",
            }}
          >
            <div style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
              {backupBannerUrgent ? "🔐" : "💾"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {backupBannerUrgent
                  ? "Back up your wallet now"
                  : "Back up your wallet"}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--hint)",
                  marginTop: 2,
                  lineHeight: 1.4,
                }}
              >
                {backupBannerUrgent
                  ? "You have funds. Save your recovery phrase so you can restore the wallet if you lose Telegram access."
                  : "Save your 12-word recovery phrase so you can restore the wallet if you lose Telegram access."}
              </div>
            </div>
            <div style={{ fontSize: 16, color: "var(--hint)", flexShrink: 0 }}>›</div>
          </button>
        )}

        {showDeviceWarning && (
          <Card
            style={{
              padding: 12,
              marginBottom: 12,
              background: "rgba(255, 200, 80, 0.08)",
              border: "1px solid rgba(255, 200, 80, 0.25)",
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
            }}
          >
            <div style={{ fontSize: 18, lineHeight: 1 }}>⚠️</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                Your device may be slow during sends
              </div>
              <div style={{ fontSize: 12, color: "var(--hint)" }}>
                Proofs can take 15+ seconds.
                {assessment.reason ? ` Detected: ${assessment.reason}.` : ""}
              </div>
            </div>
            <button
              onClick={dismissWarning}
              aria-label="Dismiss warning"
              style={{
                background: "transparent",
                color: "var(--hint)",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </Card>
        )}

        <Card style={{ padding: 18 }}>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "var(--hint)",
            }}
          >
            Shielded balance
          </div>
          {showBalanceSkeleton ? (
            <>
              <div style={{ marginTop: 6 }}>
                <Skeleton width={220} height={32} />
              </div>
              <div style={{ marginTop: 10 }}>
                <Skeleton width={120} height={14} />
              </div>
              <div style={{ marginTop: 10 }}>
                <Skeleton width={140} height={12} />
              </div>
            </>
          ) : (
            <>
              {(() => {
                const { whole, fractional } = formatBalanceZec(displayZats);
                return (
                  <div
                    // `flashKey` lets us re-trigger the CSS animation by
                    // remounting the className via the key prop — the
                    // animation only plays on initial mount of each node.
                    key={flashKey}
                    className={flashKey > 0 ? "balance-flash" : undefined}
                    style={{
                      fontSize: 32,
                      fontWeight: 700,
                      marginTop: 4,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {whole}
                    <span style={{ color: "var(--hint)", fontWeight: 500 }}>
                      .{fractional}
                    </span>{" "}
                    <span style={{ fontSize: 14, color: "var(--hint)", fontWeight: 500 }}>
                      ZEC
                    </span>
                  </div>
                );
              })()}
              {zecUsd != null && (
                <div
                  style={{
                    fontSize: 14,
                    color: "var(--text-soft)",
                    fontWeight: 500,
                    marginTop: 2,
                  }}
                >
                  {zatsToUsd(displayZats, zecUsd) ?? ""}
                  <span style={{ fontSize: 11, color: "var(--hint)", marginLeft: 8 }}>
                    1 ZEC = {formatUsd(zecUsd)}
                  </span>
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 12, color: "var(--hint)" }}>
                {balance.unspentNoteCount} note{balance.unspentNoteCount === 1 ? "" : "s"}
                {balance.lastSyncedHeight > 0
                  ? ` • synced to ${balance.lastSyncedHeight.toLocaleString()}`
                  : ""}
                {polling ? " • syncing…" : ""}
              </div>
              {/* "Recover balance" escape hatch. Visible whenever the
                  wallet's display might be wrong:
                    - any pending ZEC tx is sitting in history (relay
                      may have silently dropped it), OR
                    - the scanner says we're synced but the balance is
                      zero AND we've never seen a note (which matches
                      both "fresh wallet" AND "old build deleted the
                      notes on a broken broadcast" — the user has no
                      way to tell those apart from the UI, so always
                      offer the recovery).
                  Tapping it does a full forceFreshSync, which is
                  idempotent if local state was already correct. */}
              {(txs.some(
                (tx) =>
                  (tx.status ?? "pending") === "pending" &&
                  (!tx.origin_chain || tx.origin_chain === "zec"),
              ) ||
                (balance.lastSyncedHeight > 0 &&
                  balance.netBalanceZats === 0 &&
                  balance.unspentNoteCount === 0)) && (
                <button
                  type="button"
                  disabled={recovering}
                  onClick={async () => {
                    setRecovering(true);
                    haptic("medium");
                    try {
                      const { netBalanceZats } = await recoverPendingSpends();

                      // After the on-chain rebuild, reconcile every
                      // local ZEC tx that's still "pending" against a
                      // public indexer. If Blockchair has never seen
                      // the txid, the relay accepted but dropped it —
                      // flip the row to "failed" so the user gets
                      // honest feedback instead of a forever-spinner.
                      // Only consider txs that have aged past 90 s
                      // (~one ZEC block) to avoid racing a freshly
                      // broadcast tx that just hasn't propagated yet.
                      const FAIL_MIN_AGE_MS = 90_000;
                      const candidates = recentTransactions(50).filter(
                        (tx) =>
                          (tx.status ?? "pending") === "pending" &&
                          (!tx.origin_chain || tx.origin_chain === "zec") &&
                          Date.now() - tx.broadcasted_at > FAIL_MIN_AGE_MS,
                      );
                      let failedCount = 0;
                      if (candidates.length > 0) {
                        const statuses = await checkZcashTxStatusBatch(
                          candidates.map((tx) => tx.txid),
                        );
                        for (const tx of candidates) {
                          const s = statuses.get(tx.txid);
                          if (s === "missing") {
                            updateTransactionStatus(tx.txid, {
                              status: "failed",
                              swap_stage: "broadcast not propagated",
                            });
                            failedCount++;
                          }
                        }
                        // Refresh the in-memory tx list so the UI
                        // shows the new statuses without waiting for
                        // the next poll tick.
                        if (failedCount > 0) setTxs(recentTransactions(10));
                      }

                      const zec = (netBalanceZats / 1e8)
                        .toFixed(4)
                        .replace(/\.?0+$/, "");
                      const head =
                        netBalanceZats > 0
                          ? `Synced from chain — ${zec} ZEC`
                          : "Synced from chain — no notes found";
                      const tail =
                        failedCount > 0
                          ? ` · ${failedCount} stuck tx${failedCount === 1 ? "" : "s"} marked failed`
                          : "";
                      setRecoverToast(`${head}${tail}`);
                      setTimeout(() => setRecoverToast(null), 4500);
                    } finally {
                      setRecovering(false);
                    }
                  }}
                  style={{
                    marginTop: 10,
                    background: "transparent",
                    border: "1px solid var(--hairline, rgba(255,255,255,0.06))",
                    color: "var(--text-soft)",
                    cursor: recovering ? "default" : "pointer",
                    fontFamily: "var(--font-text)",
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "6px 12px",
                    borderRadius: 999,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    opacity: recovering ? 0.6 : 1,
                  }}
                  aria-label="Recover balance from a stuck pending transaction"
                >
                  {recovering ? "Recovering…" : "↺ Recover balance"}
                </button>
              )}
              {recoverToast && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 11,
                    color: "var(--success, #34C759)",
                    fontWeight: 500,
                  }}
                >
                  {recoverToast}
                </div>
              )}
            </>
          )}
          {state.kind === "error" && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--destructive, #d33)" }}>
              {state.message}
            </div>
          )}
          {error && (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--destructive, #d33)" }}>
              {error}
            </div>
          )}
        </Card>

        {(!keys || state.kind === "deriving") ? (
          <Card style={{ padding: 14, marginTop: 12 }}>
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: 1,
                color: "var(--hint)",
                marginBottom: 8,
              }}
            >
              Your address
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Skeleton width="100%" height={14} />
              </div>
              <Skeleton width={64} height={24} radius={999} />
            </div>
          </Card>
        ) : (
          <Card style={{ padding: 14, marginTop: 12 }}>
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: 1,
                color: "var(--hint)",
                marginBottom: 6,
              }}
            >
              Your address
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 12,
                  fontFamily: "monospace",
                  color: "var(--text-soft)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={keys.unifiedAddress}
              >
                {truncateAddr(keys.unifiedAddress)}
              </div>
              <button
                onClick={() => void copyUnifiedAddress()}
                style={{
                  flexShrink: 0,
                  padding: "5px 12px",
                  background: uaCopied
                    ? "rgba(46, 187, 96, 0.18)"
                    : "var(--bg-2, rgba(255,255,255,0.06))",
                  color: uaCopied ? "var(--success, #34C759)" : "var(--text)",
                  border: "1px solid var(--hairline, rgba(255,255,255,0.08))",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "var(--font-text)",
                  transition: "background 0.15s",
                }}
              >
                {uaCopied ? "✓ Copied" : "Copy"}
              </button>
            </div>
          </Card>
        )}

        {backedUp && onOpenBackup && (
          <button
            onClick={() => {
              haptic("light");
              onOpenBackup();
            }}
            style={{
              width: "100%",
              marginTop: 12,
              padding: "10px 14px",
              background: "transparent",
              color: "var(--hint)",
              border: "1px solid var(--hairline, rgba(255,255,255,0.06))",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "var(--font-text)",
            }}
          >
            Show recovery phrase
          </button>
        )}

        <EvmHoldingsCard />

        {isEmpty && keys ? (
          <FundWalletCard
            unifiedAddress={keys.unifiedAddress}
            uaCopied={uaCopied}
            onCopy={copyUnifiedAddress}
          />
        ) : (
          (() => {
            const activity = buildActivity(txs, incoming);
            if (activity.length === 0) return null;
            return (
              <>
                <div
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    color: "var(--hint)",
                    margin: "24px 4px 10px",
                    fontWeight: 600,
                  }}
                >
                  Recent activity
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {activity.map((entry) =>
                    entry.direction === "out" ? (
                      <TxRow
                        key={`out-${entry.tx.id}`}
                        tx={entry.tx}
                        zecUsd={zecUsd}
                        onOpen={() => setDetail({ kind: "out", tx: entry.tx })}
                      />
                    ) : (
                      <IncomingRow
                        key={`in-${entry.note.nullifier}`}
                        note={entry.note}
                        zecUsd={zecUsd}
                        onOpen={() => setDetail({ kind: "in", note: entry.note })}
                      />
                    ),
                  )}
                </div>
              </>
            );
          })()
        )}
      </div>
      </PullToRefresh>
      <ActivityDetailSheet
        entry={detail}
        zecUsd={zecUsd}
        onClose={() => setDetail(null)}
      />

    </TgScreen>
  );
}

// ─── Pull-to-refresh ───────────────────────────────────────────────────
// A tiny gesture wrapper. It watches the nearest scrollable ancestor
// (TgScreen's `.tg-scroll`); when the user drags down past `threshold`
// while scrollTop === 0, we surface a small "release to refresh"
// indicator and fire onRefresh() on release. No spring physics, no
// rubber-banding — keeps the implementation honest about scope.
const PTR_THRESHOLD = 70;
const PTR_MAX = 110;
function PullToRefresh({
  children,
  onRefresh,
  disabled,
}: {
  children: ReactNode;
  onRefresh: () => Promise<void> | void;
  disabled?: boolean;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const startYRef = useRef<number | null>(null);
  const draggingRef = useRef<boolean>(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Find the nearest scroll container once mounted. `.tg-scroll` is the
  // class TgScreen puts on its content scroller.
  useEffect(() => {
    let el: HTMLElement | null = wrapRef.current;
    while (el && !el.classList.contains("tg-scroll")) {
      el = el.parentElement;
    }
    scrollerRef.current = el;
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    if (disabled || refreshing) return;
    const sc = scrollerRef.current;
    if (!sc || sc.scrollTop > 0) return;
    startYRef.current = e.touches[0]?.clientY ?? null;
    draggingRef.current = true;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!draggingRef.current || startYRef.current == null) return;
    const sc = scrollerRef.current;
    if (sc && sc.scrollTop > 0) {
      // User scrolled down within the list — abandon the pull gesture.
      draggingRef.current = false;
      setPull(0);
      return;
    }
    const dy = (e.touches[0]?.clientY ?? 0) - startYRef.current;
    if (dy <= 0) {
      setPull(0);
      return;
    }
    // Resistance: 0.55 feels good without a spring sim.
    const damped = Math.min(PTR_MAX, dy * 0.55);
    setPull(damped);
  };
  const onTouchEnd = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    startYRef.current = null;
    const past = pull >= PTR_THRESHOLD;
    if (past && !disabled && !refreshing) {
      setRefreshing(true);
      // Snap indicator to a small "active" height while the refresh runs.
      setPull(48);
      Promise.resolve(onRefresh())
        .catch(() => {
          /* swallow — surfaced via balance-context error */
        })
        .finally(() => {
          // Minimum 600ms so the spinner is perceptible even on a
          // network refresh that returns in <100ms.
          window.setTimeout(() => {
            setRefreshing(false);
            setPull(0);
          }, 600);
        });
    } else {
      setPull(0);
    }
  };

  const indicatorOpacity = Math.min(1, pull / PTR_THRESHOLD);
  const armed = pull >= PTR_THRESHOLD;
  return (
    <div
      ref={wrapRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      style={{
        // We move the children down by `pull` so the indicator can sit
        // in the gap. transform avoids layout thrash compared to
        // margin/padding tweaks.
        transform: `translateY(${pull}px)`,
        transition: draggingRef.current
          ? "none"
          : "transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        willChange: "transform",
        position: "relative",
      }}
    >
      <div
        aria-hidden={!refreshing && pull === 0}
        style={{
          position: "absolute",
          top: -56,
          left: 0,
          right: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          height: 48,
          color: "var(--hint)",
          fontSize: 12,
          fontWeight: 500,
          opacity: refreshing ? 1 : indicatorOpacity,
          pointerEvents: "none",
        }}
      >
        {refreshing ? (
          <>
            <Spinner size={16} />
            <span>Refreshing…</span>
          </>
        ) : (
          <>
            <span
              style={{
                display: "inline-block",
                fontSize: 16,
                transform: `rotate(${armed ? 180 : 0}deg)`,
                transition: "transform 160ms ease",
              }}
            >
              ↻
            </span>
            <span>{armed ? "Release to refresh" : "Pull to refresh"}</span>
          </>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Onboarding card shown on the Wallet tab when there's literally
 * nothing to display — no balance, no incoming notes, no outgoing
 * history. Gives the user a way to fund the wallet without bouncing
 * over to the Receive tab on first run.
 */
function FundWalletCard({
  unifiedAddress,
  uaCopied,
  onCopy,
}: {
  unifiedAddress: string;
  uaCopied: boolean;
  onCopy: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, unifiedAddress, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 160,
      color: { dark: "#000000", light: "#ffffff" },
    }).catch((e: unknown) => {
      // eslint-disable-next-line no-console
      console.warn("[wallet/fund] QR render failed", e);
    });
  }, [unifiedAddress]);

  return (
    <Card
      style={{
        marginTop: 20,
        padding: 18,
        background:
          "linear-gradient(180deg, rgba(120,160,255,0.06) 0%, var(--bg-2, rgba(255,255,255,0.04)) 100%)",
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700 }}>Fund this wallet</div>
      <div
        style={{
          fontSize: 12,
          color: "var(--hint)",
          marginTop: 4,
          lineHeight: 1.4,
        }}
      >
        Send shielded ZEC to your address to get started.
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: 14,
        }}
      >
        <div
          style={{
            padding: 8,
            background: "white",
            borderRadius: 8,
          }}
        >
          <canvas ref={canvasRef} aria-label="Unified Address QR code" />
        </div>
      </div>
      <div
        style={{
          marginTop: 12,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            fontFamily: "monospace",
            color: "var(--text-soft)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={unifiedAddress}
        >
          {truncateAddr(unifiedAddress)}
        </div>
        <button
          onClick={onCopy}
          style={{
            flexShrink: 0,
            padding: "5px 12px",
            background: uaCopied
              ? "rgba(46, 187, 96, 0.18)"
              : "var(--bg-2, rgba(255,255,255,0.06))",
            color: uaCopied ? "var(--success, #34C759)" : "var(--text)",
            border: "1px solid var(--hairline, rgba(255,255,255,0.08))",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "var(--font-text)",
            transition: "background 0.15s",
          }}
        >
          {uaCopied ? "✓ Copied" : "Copy"}
        </button>
      </div>
      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          color: "var(--hint)",
          lineHeight: 1.4,
        }}
      >
        Open Receive tab for the full QR and share options.
      </div>
    </Card>
  );
}

// ─── Activity row helpers ──────────────────────────────────────────────

function ActivityCard({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic("light");
        onClick();
      }}
      style={{
        all: "unset",
        display: "block",
        width: "100%",
        cursor: "pointer",
        background: "var(--bg-2, #161A21)",
        border: "1px solid var(--hairline, rgba(255,255,255,0.045))",
        borderRadius: 14,
        padding: "14px 16px",
        boxSizing: "border-box",
        transition: "background 120ms ease, transform 120ms ease",
      }}
      onMouseDown={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          "var(--bg-3, #1F242D)";
      }}
      onMouseUp={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          "var(--bg-2, #161A21)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          "var(--bg-2, #161A21)";
      }}
    >
      {children}
    </button>
  );
}

function DirectionGlyph({
  kind,
}: {
  kind: "send" | "swap" | "receive";
}) {
  const tint =
    kind === "receive"
      ? "var(--success, #34C759)"
      : kind === "swap"
        ? "var(--accent, #F0B400)"
        : "var(--link, #6CB0FF)";
  const glyph = kind === "receive" ? "↙" : kind === "swap" ? "⇄" : "↗";
  return (
    <div
      aria-hidden
      style={{
        width: 34,
        height: 34,
        borderRadius: 999,
        background: "color-mix(in oklab, currentColor 12%, transparent)",
        color: tint,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {glyph}
    </div>
  );
}

function TxRow({
  tx,
  zecUsd,
  onOpen,
}: {
  tx: TxRecord;
  zecUsd: number | null;
  onOpen: () => void;
}) {
  // EVM swaps don't denominate in zats — amount_zats is 0 and the real
  // source amount lives in `from_amount` (already human-decimal) +
  // `from_asset` (for the ticker). ZEC sends/swaps keep using the
  // zats path. We branch ONCE here so the row never shows "−0 ZEC"
  // for an EVM origin.
  const isEvmOrigin =
    tx.kind === "swap" && tx.origin_chain && tx.origin_chain !== "zec";
  const amount = isEvmOrigin
    ? tx.from_amount ?? "0"
    : (tx.amount_zats / 1e8).toFixed(8);
  const amountTicker = isEvmOrigin
    ? assetTicker(tx.from_asset ?? null)
    : "ZEC";
  const usd = isEvmOrigin ? null : zatsToUsd(tx.amount_zats, zecUsd);
  const status: TxStatus = tx.status ?? "pending";
  const isSwap = tx.kind === "swap";
  const title = isSwap
    ? `Swap → ${assetTicker(tx.swap_to_asset)}`
    : tx.recipient_username
      ? `Sent → @${tx.recipient_username}`
      : `Sent → ${truncateAddr(tx.recipient, 8, 6)}`;
  const memo = tx.memo?.trim() || null;
  return (
    <ActivityCard onClick={onOpen}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <DirectionGlyph kind={isSwap ? "swap" : "send"} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--text)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              color: "var(--hint)",
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            <StatusDot status={status} />
            <StatusText status={status} stage={tx.swap_stage} />
            <span aria-hidden>·</span>
            <span>{timeAgo(tx.broadcasted_at)}</span>
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
            −{amount}
            <span
              style={{
                fontSize: 11,
                color: "var(--hint)",
                fontWeight: 500,
                marginLeft: 3,
              }}
            >
              {amountTicker}
            </span>
          </div>
          {usd && (
            <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 2 }}>
              {usd}
            </div>
          )}
        </div>
      </div>
      {memo && (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: "var(--text-soft)",
            background: "var(--bg-3, #1F242D)",
            borderRadius: 8,
            padding: "8px 10px",
            lineHeight: 1.45,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: "var(--hint)",
              marginRight: 6,
            }}
          >
            memo
          </span>
          {memo}
        </div>
      )}
    </ActivityCard>
  );
}

function IncomingRow({
  note,
  zecUsd,
  onOpen,
}: {
  note: IncomingNote;
  zecUsd: number | null;
  onOpen: () => void;
}) {
  const amount = (note.value_zats / 1e8).toFixed(8);
  const usd = zatsToUsd(note.value_zats, zecUsd);
  const memo = note.memo?.trim() || null;
  return (
    <ActivityCard onClick={onOpen}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <DirectionGlyph kind="receive" />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "var(--text)",
            }}
          >
            Received
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              color: "var(--hint)",
              display: "flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
            }}
          >
            <span>block {note.height.toLocaleString()}</span>
            <span aria-hidden>·</span>
            <span>{timeAgo(note.observed_at)}</span>
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "var(--success, #34C759)",
            }}
          >
            +{amount}
            <span
              style={{
                fontSize: 11,
                color: "var(--hint)",
                fontWeight: 500,
                marginLeft: 3,
              }}
            >
              ZEC
            </span>
          </div>
          {usd && (
            <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 2 }}>
              {usd}
            </div>
          )}
        </div>
      </div>
      {memo && (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: "var(--text-soft)",
            background: "rgba(52, 199, 89, 0.06)",
            border: "1px solid rgba(52, 199, 89, 0.18)",
            borderRadius: 8,
            padding: "8px 10px",
            lineHeight: 1.45,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: "var(--success, #34C759)",
              marginRight: 6,
            }}
          >
            memo
          </span>
          {memo}
        </div>
      )}
    </ActivityCard>
  );
}

// ─── Detail sheet ──────────────────────────────────────────────────────

/**
 * Pick the right block-explorer link(s) for a tx based on the chain
 * its broadcast happened on. Defaults to Zcash for unknown / legacy
 * rows (back-compat — pre-multi-asset history didn't tag chains).
 */
function explorerLinksFor(
  origin: string,
  txid: string,
): Array<{ label: string; url: string }> {
  switch (origin) {
    case "ETH":
      return [{ label: "Etherscan", url: `https://etherscan.io/tx/${txid}` }];
    case "ARB":
      return [{ label: "Arbiscan", url: `https://arbiscan.io/tx/${txid}` }];
    case "BASE":
      return [{ label: "Basescan", url: `https://basescan.org/tx/${txid}` }];
    case "OP":
      return [
        {
          label: "Optimistic Etherscan",
          url: `https://optimistic.etherscan.io/tx/${txid}`,
        },
      ];
    case "POLYGON":
      return [
        { label: "PolygonScan", url: `https://polygonscan.com/tx/${txid}` },
      ];
    default:
      // ZEC: Blockchair has the broadest indexing; MAYAScan is rendered
      // separately as a sibling button when this is a ZEC swap (the
      // caller branches on swap-direction).
      return [
        {
          label: "View on Blockchair",
          url: `https://blockchair.com/zcash/transaction/${txid}`,
        },
      ];
  }
}

function openExplorer(url: string) {
  const wa = window.Telegram?.WebApp as
    | (TelegramWebApp & { openLink?: (u: string) => void })
    | undefined;
  if (wa?.openLink) {
    wa.openLink(url);
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function ActivityDetailSheet({
  entry,
  zecUsd,
  onClose,
}: {
  entry:
    | { kind: "out"; tx: TxRecord }
    | { kind: "in"; note: IncomingNote }
    | null;
  zecUsd: number | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async (s: string) => {
    try {
      await navigator.clipboard.writeText(s);
      setCopied(true);
      haptic("success");
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* best-effort */
    }
  };

  // Normalise into a single shape so the layout doesn't fork by kind.
  // Computed inside the conditional content below; we early-return on no
  // entry so the Drawer can still animate open/closed cleanly.
  const view = entry
    ? entry.kind === "in"
      ? (() => {
          const n = entry.note;
          return {
            direction: "receive" as const,
            sign: "+",
            amountZats: n.value_zats,
            title: "Received",
            timestamp: n.observed_at,
            height: n.height as number | null,
            txid: n.txid,
            memo: n.memo?.trim() || null,
            recipient: null as string | null,
            recipientLabel: null as string | null,
            status: "confirmed" as TxStatus,
            stage: null as string | null,
            fee: null as number | null,
            swapTo: null as { asset: string; address: string } | null,
          };
        })()
      : (() => {
          const tx = entry.tx;
          return {
            direction: (tx.kind === "swap" ? "swap" : "send") as
              | "send"
              | "swap"
              | "receive",
            sign: "−",
            amountZats: tx.amount_zats,
            title: tx.kind === "swap" ? "Swap" : "Sent",
            timestamp: tx.broadcasted_at,
            height: null as number | null,
            txid: tx.txid,
            memo: tx.memo?.trim() || null,
            recipient: tx.recipient,
            recipientLabel: tx.recipient_username
              ? `@${tx.recipient_username}`
              : null,
            status: tx.status ?? ("pending" as TxStatus),
            stage: tx.swap_stage ?? null,
            fee: tx.fee_zats,
            swapTo:
              tx.kind === "swap" && tx.swap_to_asset
                ? { asset: tx.swap_to_asset, address: tx.swap_to_address ?? "" }
                : null,
            originChain: tx.origin_chain ?? "zec",
            fromAsset: tx.from_asset ?? null,
            fromAmount: tx.from_amount ?? null,
          };
        })()
    : null;

  // EVM swaps carry their source amount in `fromAmount` (human-decimal
  // already) + a ticker derived from `fromAsset`. ZEC sends/receives
  // keep using the zats math. Branch here so the hero amount on an
  // EVM swap doesn't render as "0 ZEC".
  const isEvmOriginView =
    view != null &&
    "originChain" in view &&
    view.originChain &&
    view.originChain !== "zec";
  const amount = !view
    ? "0"
    : isEvmOriginView
      ? ((view as { fromAmount?: string | null }).fromAmount ?? "0")
      : (view.amountZats / 1e8).toFixed(8);
  const amountTickerSheet = isEvmOriginView
    ? assetTicker((view as { fromAsset?: string | null }).fromAsset ?? null)
    : "ZEC";
  // USD value: only know it for ZEC (we have zecUsd). EVM source-amount
  // USD would need a per-asset price feed we don't ship; hide for now
  // rather than show a bogus "$0".
  const usd =
    view && !isEvmOriginView ? zatsToUsd(view.amountZats, zecUsd) : null;
  // Network fee was a ZEC-denominated value too. For EVM swaps the
  // gas was paid in native EVM asset, captured at sign time and not
  // surfaced through tx-history (fee_zats=0). Hide rather than show
  // "0 ZEC" which would be misleading.
  const feeZec =
    view?.fee != null && !isEvmOriginView ? (view.fee / 1e8).toFixed(8) : null;
  const amountColor = view
    ? view.direction === "receive"
      ? "var(--success, #34C759)"
      : "var(--text)"
    : "var(--text)";

  return (
    <Drawer.Root
      open={!!entry}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Drawer.Portal>
        <Drawer.Overlay
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 50,
          }}
        />
        <Drawer.Content
          aria-describedby={undefined}
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 51,
            display: "flex",
            flexDirection: "column",
            maxHeight: "90dvh",
            background: "var(--bg, #0E1116)",
            color: "var(--text)",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            boxShadow: "0 -12px 28px rgba(0,0,0,0.4)",
            outline: "none",
            // Explicitly carry the in-app font stack — vaul portals this
            // into document.body, outside `.tg-app`, so without this we
            // inherit the UA default (Times on most iOS WebViews).
            fontFamily: "var(--font-text)",
            fontSize: 15,
            lineHeight: 1.35,
            letterSpacing: "-0.005em",
            fontVariantNumeric: "tabular-nums",
            WebkitFontSmoothing: "antialiased",
          }}
        >
          {/* Vaul's built-in drag handle. Tapping or dragging this
              dismisses; the inner scroll region scrolls independently
              when content overflows. */}
          <div
            style={{
              padding: "10px 0 6px",
              flexShrink: 0,
              cursor: "grab",
              touchAction: "none",
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                borderRadius: 999,
                background: "var(--hint-soft, rgba(255,255,255,0.32))",
                margin: "0 auto",
              }}
            />
          </div>

          {/* Scrollable body — vaul detects whether this is scrolled to
              the top before deciding to dismiss on drag-down, so scroll
              behaviour and drag-to-close coexist properly. */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              padding:
                "4px 18px max(22px, env(safe-area-inset-bottom)) 18px",
            }}
          >
            {view && (
              <>
                {/* Title is required for a11y but visually hidden — the
                    header below already carries the same information. */}
                <Drawer.Title
                  style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    padding: 0,
                    margin: -1,
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    whiteSpace: "nowrap",
                    border: 0,
                  }}
                >
                  {view.title} {view.sign}
                  {amount} {amountTickerSheet}
                </Drawer.Title>

                {/* Centered hero — direction glyph, big amount, USD, status pill */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    padding: "8px 0 20px",
                    position: "relative",
                  }}
                >
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    style={{
                      all: "unset",
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 32,
                      height: 32,
                      borderRadius: 999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "var(--hint)",
                      fontSize: 18,
                      background: "var(--bg-2, #161A21)",
                    }}
                  >
                    ✕
                  </button>
                  <div style={{ transform: "scale(1.4)", marginBottom: 14 }}>
                    <DirectionGlyph kind={view.direction} />
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: "var(--hint)",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    {view.title}
                  </div>
                  <div
                    style={{
                      fontSize: 34,
                      fontWeight: 700,
                      color: amountColor,
                      letterSpacing: "-0.02em",
                      marginTop: 6,
                      lineHeight: 1.1,
                    }}
                  >
                    {view.sign}
                    {amount}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--hint)",
                      marginTop: 4,
                      fontWeight: 500,
                      letterSpacing: 0.5,
                    }}
                  >
                    {amountTickerSheet}
                    {usd ? `  ·  ${view.sign}${usd}` : ""}
                  </div>
                  <div
                    style={{
                      marginTop: 14,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "4px 10px",
                      borderRadius: 999,
                      background:
                        view.status === "confirmed"
                          ? "rgba(52,199,89,0.10)"
                          : view.status === "failed"
                            ? "rgba(255,59,48,0.12)"
                            : "rgba(255,176,32,0.10)",
                      color:
                        view.status === "confirmed"
                          ? "var(--success, #34C759)"
                          : view.status === "failed"
                            ? "var(--danger, #FF3B30)"
                            : "var(--warning, #FFB020)",
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    <StatusDot status={view.status} />
                    <StatusText status={view.status} stage={view.stage} />
                  </div>
                </div>

                {/* Details list — no card wrapper; rows with subtle dividers */}
                <div
                  style={{
                    padding: "0 4px",
                    borderTop: "1px solid var(--hairline, rgba(255,255,255,0.045))",
                  }}
                >
                  {view.recipientLabel && (
                    <DetailRow label="Recipient" value={view.recipientLabel} />
                  )}
                  {view.recipient && (
                    <DetailRow
                      label={view.recipientLabel ? "Address" : "Recipient"}
                      value={truncateAddr(view.recipient, 10, 8)}
                      fullValue={view.recipient}
                      copyable
                      onCopy={copy}
                      mono
                    />
                  )}
                  {view.swapTo && (
                    <>
                      <DetailRow
                        label="Swap to"
                        value={`${assetTicker(view.swapTo.asset)} on ${view.swapTo.asset.split(".")[0] ?? "?"}`}
                      />
                      {view.swapTo.address && (
                        <DetailRow
                          label="Destination"
                          value={truncateAddr(view.swapTo.address, 10, 8)}
                          fullValue={view.swapTo.address}
                          copyable
                          onCopy={copy}
                          mono
                        />
                      )}
                    </>
                  )}
                  <DetailRow
                    label="Transaction"
                    value={truncateAddr(view.txid, 10, 8)}
                    fullValue={view.txid}
                    copyable
                    onCopy={copy}
                    mono
                  />
                  {view.height != null && (
                    <DetailRow
                      label="Block"
                      value={view.height.toLocaleString()}
                    />
                  )}
                  <DetailRow
                    label="Time"
                    value={new Date(view.timestamp).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  />
                  {feeZec && <DetailRow label="Network fee" value={`${feeZec} ZEC`} />}
                </div>

                {/* Memo */}
                {view.memo && (
                  <div style={{ marginTop: 20 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--hint)",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        fontWeight: 600,
                        marginBottom: 8,
                        padding: "0 4px",
                      }}
                    >
                      Memo
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        color: "var(--text)",
                        background:
                          view.direction === "receive"
                            ? "rgba(52,199,89,0.07)"
                            : "var(--bg-2, #161A21)",
                        border: `1px solid ${
                          view.direction === "receive"
                            ? "rgba(52,199,89,0.20)"
                            : "var(--hairline, rgba(255,255,255,0.06))"
                        }`,
                        borderRadius: 12,
                        padding: "14px 16px",
                        lineHeight: 1.55,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {view.memo}
                    </div>
                  </div>
                )}

                {/* Copy toast */}
                {copied && (
                  <div
                    style={{
                      position: "fixed",
                      left: "50%",
                      bottom: "calc(env(safe-area-inset-bottom) + 24px)",
                      transform: "translateX(-50%)",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "var(--success, #34C759)",
                      background: "var(--bg-2, #161A21)",
                      border: "1px solid rgba(52,199,89,0.30)",
                      borderRadius: 999,
                      padding: "6px 14px",
                      pointerEvents: "none",
                      zIndex: 60,
                    }}
                  >
                    ✓ Copied
                  </div>
                )}

                {/* Explorer button row.
                    Branches on origin chain so an EVM swap doesn't open
                    Blockchair-Zcash with a 0x-prefixed hash. */}
                <div
                  style={{
                    marginTop: 24,
                    display: "flex",
                    gap: 10,
                  }}
                >
                  {(() => {
                    const origin =
                      "originChain" in view ? view.originChain : "zec";
                    const explorers = explorerLinksFor(origin, view.txid);
                    return explorers.map((ex) => (
                      <ExplorerButton
                        key={ex.label}
                        label={ex.label}
                        onClick={() => {
                          haptic("light");
                          openExplorer(ex.url);
                        }}
                      />
                    ));
                  })()}
                  {view.direction === "swap" &&
                    ("originChain" in view ? view.originChain : "zec") ===
                      "zec" && (
                      <ExplorerButton
                        label="MAYAScan"
                        onClick={() => {
                          haptic("light");
                          openExplorer(
                            `https://www.mayascan.org/tx/${view.txid}`,
                          );
                        }}
                      />
                    )}
                </div>
              </>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function DetailRow({
  label,
  value,
  fullValue,
  copyable,
  onCopy,
  mono,
}: {
  label: string;
  value: string;
  fullValue?: string;
  copyable?: boolean;
  onCopy?: (s: string) => void;
  mono?: boolean;
}) {
  const clickable = copyable && fullValue && onCopy;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        padding: "14px 0",
        borderBottom: "1px solid var(--hairline, rgba(255,255,255,0.045))",
      }}
    >
      <span style={{ fontSize: 13, color: "var(--hint)", flexShrink: 0, fontWeight: 500 }}>
        {label}
      </span>
      <button
        type="button"
        onClick={clickable ? () => onCopy!(fullValue!) : undefined}
        disabled={!clickable}
        style={{
          all: "unset",
          fontSize: 14,
          fontFamily: mono ? "var(--font-mono, monospace)" : undefined,
          color: "var(--text)",
          textAlign: "right",
          wordBreak: "break-all",
          cursor: clickable ? "pointer" : "default",
          maxWidth: "65%",
          fontWeight: mono ? 500 : 500,
          letterSpacing: mono ? "-0.005em" : undefined,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
        aria-label={clickable ? `Copy ${label}` : undefined}
      >
        <span>{value}</span>
        {clickable && (
          <span
            aria-hidden
            style={{
              fontSize: 12,
              color: "var(--hint)",
              opacity: 0.6,
              flexShrink: 0,
            }}
          >
            ⎘
          </span>
        )}
      </button>
    </div>
  );
}

function ExplorerButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: "unset",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        flex: 1,
        boxSizing: "border-box",
        cursor: "pointer",
        background: "var(--btn-primary-bg, #FFFFFF)",
        color: "var(--btn-primary-fg, #0E1116)",
        borderRadius: 14,
        padding: "14px 16px",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        textAlign: "center",
        transition: "opacity 120ms ease, transform 120ms ease",
      }}
      onMouseDown={(e) => {
        (e.currentTarget as HTMLButtonElement).style.opacity = "0.8";
      }}
      onMouseUp={(e) => {
        (e.currentTarget as HTMLButtonElement).style.opacity = "1";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.opacity = "1";
      }}
    >
      <span>{label}</span>
      <span aria-hidden style={{ fontSize: 12, opacity: 0.7 }}>↗</span>
    </button>
  );
}

function StatusDot({ status }: { status: TxStatus }) {
  const color =
    status === "confirmed"
      ? "var(--success, #34C759)"
      : status === "failed"
        ? "var(--danger, #FF3B30)"
        : "var(--warning, #FFB020)";
  return (
    <span
      aria-label={`status-${status}`}
      className={status === "pending" ? "dot-pulse" : undefined}
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: 999,
        background: color,
        flexShrink: 0,
      }}
    />
  );
}

function StatusText({ status, stage }: { status: TxStatus; stage?: string | null }) {
  const label =
    status === "confirmed"
      ? "confirmed"
      : status === "failed"
        ? "failed"
        : stage ?? "pending";
  return <span>{label}</span>;
}

function assetTicker(assetId: string | null): string {
  if (!assetId) return "?";
  return assetId.split(".")[1]?.split("-")[0] ?? assetId;
}

function timeAgo(ts: number): string {
  const dt = Math.max(0, Date.now() - ts);
  const m = Math.floor(dt / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// ─── Multi-chain (EVM) holdings ────────────────────────────────────────
// Read-only surface: derive the BIP-39 → EVM address on first unlock,
// pull balances for the top 5 EVM chains from LeoKit. Rows expand
// inline to show per-token detail. No signing — sends/swaps stay on
// the existing Zcash code paths.

function EvmHoldingsCard() {
  const { address, holdings, loading, error, refresh } = useEvmHoldings();
  const [copied, setCopied] = useState(false);
  // Track which chain rows are expanded; keyed by chain id so the
  // selection survives a balance refresh.
  const [expanded, setExpanded] = useState<Record<EvmChainId, boolean>>(() =>
    EVM_CHAINS.reduce(
      (acc, c) => ({ ...acc, [c.id]: false }),
      {} as Record<EvmChainId, boolean>,
    ),
  );

  const copyAddress = async () => {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      haptic("success");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* best-effort */
    }
  };

  const toggleChain = (id: EvmChainId) => {
    haptic("light");
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // We don't render until we at least have a derived address. Before
  // that the card would be meaningless and the layout would jump
  // around as derivation completes anyway.
  if (!address) return null;

  const showSkeleton = holdings === null && loading;

  return (
    <Card style={{ padding: "14px 16px", marginTop: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "var(--hint)",
            fontWeight: 600,
            flex: 1,
          }}
        >
          Multi-chain holdings
        </div>
        <div
          style={{
            fontSize: 12,
            fontFamily: "monospace",
            color: "var(--text-soft)",
          }}
          title={address}
        >
          {truncateAddr(address)}
        </div>
        <button
          onClick={() => void copyAddress()}
          style={{
            flexShrink: 0,
            padding: "4px 10px",
            background: copied
              ? "rgba(46, 187, 96, 0.18)"
              : "var(--bg-2, rgba(255,255,255,0.06))",
            color: copied ? "var(--success, #34C759)" : "var(--text)",
            border: "1px solid var(--hairline, rgba(255,255,255,0.08))",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "var(--font-text)",
          }}
        >
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>

      {/* Total */}
      <div style={{ marginBottom: 14 }}>
        {showSkeleton ? (
          <Skeleton width={160} height={28} />
        ) : (
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--text)",
            }}
          >
            {formatUsd(holdings?.total_usd ?? 0)}
          </div>
        )}
        <div
          style={{
            fontSize: 11,
            color: "var(--hint)",
            marginTop: 2,
          }}
        >
          across {EVM_CHAINS.length} chains
          {loading && holdings ? " • refreshing…" : ""}
        </div>
      </div>

      {/* Error */}
      {error && !holdings && (
        <div
          style={{
            padding: 12,
            background: "rgba(239, 68, 68, 0.08)",
            border: "1px solid rgba(239, 68, 68, 0.22)",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
              Couldn't load balances
            </div>
            <div
              style={{
                fontSize: 11,
                color: "var(--hint)",
                marginTop: 2,
                wordBreak: "break-word",
              }}
            >
              {error}
            </div>
          </div>
          <button
            onClick={() => {
              haptic("light");
              void refresh();
            }}
            style={{
              flexShrink: 0,
              padding: "5px 12px",
              background: "var(--bg-2, rgba(255,255,255,0.06))",
              color: "var(--text)",
              border: "1px solid var(--hairline, rgba(255,255,255,0.08))",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--font-text)",
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Chain rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {showSkeleton
          ? EVM_CHAINS.map((c) => (
              <div
                key={c.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 0",
                  borderBottom: "1px solid var(--hairline, rgba(255,255,255,0.045))",
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: c.color,
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: 14, color: "var(--text)", flex: 1 }}>
                  {c.name}
                </span>
                <Skeleton width={70} height={12} />
              </div>
            ))
          : (holdings?.perChain ?? []).map((row) => (
              <EvmChainRow
                key={row.chain}
                row={row}
                expanded={!!expanded[row.chain]}
                onToggle={() => toggleChain(row.chain)}
              />
            ))}
      </div>
    </Card>
  );
}

function EvmChainRow({
  row,
  expanded,
  onToggle,
}: {
  row: ChainBalances;
  expanded: boolean;
  onToggle: () => void;
}) {
  const chainMeta = EVM_CHAINS.find((c) => c.id === row.chain);
  const color = chainMeta?.color ?? "var(--hint)";
  const hasTokens = row.tokens.length > 0;
  return (
    <div
      style={{
        borderBottom: "1px solid var(--hairline, rgba(255,255,255,0.045))",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={!hasTokens}
        style={{
          all: "unset",
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "12px 0",
          cursor: hasTokens ? "pointer" : "default",
          boxSizing: "border-box",
          fontFamily: "var(--font-text)",
        }}
        aria-expanded={expanded}
      >
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: color,
            boxShadow: `0 0 0 3px ${color}33`,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--text)",
            flex: 1,
          }}
        >
          {row.chainName}
        </span>
        <span
          style={{
            fontSize: 11,
            color: "var(--hint)",
            marginRight: 6,
          }}
        >
          {row.tokens.length === 0
            ? "—"
            : `${row.tokens.length} token${row.tokens.length === 1 ? "" : "s"}`}
        </span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--text)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatUsd(row.balance_usd)}
        </span>
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 14,
            color: "var(--hint)",
            fontSize: 12,
            textAlign: "right",
            transform: expanded ? "rotate(90deg)" : "none",
            transition: "transform 160ms ease",
            opacity: hasTokens ? 1 : 0.35,
          }}
        >
          ›
        </span>
      </button>

      {expanded && hasTokens && (
        <div
          style={{
            paddingBottom: 12,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {row.tokens.map((t) => (
            <EvmTokenRow key={`${row.chain}:${t.token_symbol}`} token={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function EvmTokenRow({ token }: { token: TokenBalance }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: "var(--bg-2, rgba(255,255,255,0.04))",
        border: "1px solid var(--hairline, rgba(255,255,255,0.045))",
        borderRadius: 10,
      }}
    >
      {token.icon ? (
        <img
          src={token.icon}
          alt=""
          width={24}
          height={24}
          style={{ borderRadius: 999, flexShrink: 0 }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
          }}
        />
      ) : (
        <span
          aria-hidden
          style={{
            width: 24,
            height: 24,
            borderRadius: 999,
            background: "var(--bg-3, rgba(255,255,255,0.06))",
            flexShrink: 0,
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--text)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {token.symbol}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--hint)",
            marginTop: 2,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatTokenAmount(token.balance)} {token.symbol}
        </div>
      </div>
      <div
        style={{
          textAlign: "right",
          flexShrink: 0,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
          {formatUsd(token.balance_usd)}
        </div>
        {token.price > 0 && (
          <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 2 }}>
            {formatUsd(token.price)}
          </div>
        )}
      </div>
    </div>
  );
}

// Compact balance formatter that keeps small dust values readable
// without flooding the row when someone holds 1.2345678 ETH.
function formatTokenAmount(n: number): string {
  if (!Number.isFinite(n) || n === 0) return "0";
  if (n >= 1000) return n.toFixed(2);
  if (n >= 1) return n.toFixed(4);
  if (n >= 0.0001) return n.toFixed(6);
  return n.toExponential(2);
}
